import { useCallback, useEffect, useState } from 'react'
import './pipelineVisual.css'

const stages = [
  ['source_fetch', 'GitHub 소스 가져오기'],
  ['image_build', '컨테이너 이미지 빌드'],
  ['dev_deploy', '개발 환경 배포'],
  ['dev_verify', '개발 환경 검증'],
  ['prod_approval', '운영 배포 승인'],
  ['prod_deploy', '운영 환경 배포'],
  ['prod_verify', '운영 상태 확인'],
]
const states = { queued: '대기 중', running: '진행 중', succeeded: '성공',
  failed: '실패', blocked: '차단', not_started: '미실행' }

export default function PipelineMonitoring({ service, api }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const refresh = useCallback(async signal => {
    if (!service) return
    setLoading(true)
    try {
      const result = await api(`/services/${encodeURIComponent(service.service_id)}/pipeline`, { signal })
      if (!result.readOnly || result.executionConnected !== false) throw Error('실행 이력의 출처를 확인할 수 없습니다.')
      if (!signal?.aborted) { setData(result); setError('') }
    } catch (cause) {
      if (cause.name !== 'AbortError' && !signal?.aborted) { setData(null); setError(cause.message) }
    } finally { if (!signal?.aborted) setLoading(false) }
  }, [api, service])

  useEffect(() => {
    setData(null); setError('')
    if (!service) return undefined
    const controller = new AbortController()
    refresh(controller.signal)
    const timer = setInterval(() => refresh(controller.signal), 15000)
    return () => { clearInterval(timer); controller.abort() }
  }, [refresh, service])

  const latest = data?.recentRuns?.[0]
  const byStage = new Map((data?.events || []).map(event => [event.stage, event]))
  return <section className="deploy-panel" aria-labelledby="pipeline-heading">
    <div className="deploy-section-heading"><h2 id="pipeline-heading">배포 파이프라인 진행 이력</h2>
      <button className="deploy-new" type="button" onClick={() => refresh()} disabled={!service || loading}>이력 새로고침</button></div>
    {!service && <p>서비스를 등록하거나 선택하면 단계별 이력을 볼 수 있습니다.</p>}
    {service && <>
      <p>대상 서비스: <strong>{service.display_name}</strong> · {/^https:\/\/github\.com\/AntsNest\/[A-Za-z0-9_.-]+(?:\.git)?$/.test(service.repository_url || '')
        ? <a href={service.repository_url} target="_blank" rel="noopener noreferrer">GitHub 소스</a> : '소스 주소 확인 필요'}</p>
      <p className="deploy-warning">실행기와 에이전트 명령 연결 전입니다. 이 화면은 저장된 실행 증거만 읽으며 배포를 시작하지 않습니다.</p>
      {error && <p className="deploy-error" role="alert">{error}</p>}
      {!error && loading && !data && <p role="status">실행 이력을 읽는 중…</p>}
      {!error && data && <>
        {latest ? <p>최근 실행: {latest.run_id} · {new Date(latest.created_at).toLocaleString('ko-KR')}</p>
          : <p role="status">실행 기록이 없습니다. 서비스 등록은 소스 가져오기나 배포 완료를 뜻하지 않습니다.</p>}
        <ol className="deploy-pipeline" aria-label="파이프라인 단계별 상태">
          {stages.map(([code, label], index) => {
            const event = byStage.get(code)
            const state = Object.prototype.hasOwnProperty.call(states, event?.state) ? event.state : 'not_started'
            return <li key={code} className={`deploy-pipeline-${state}`}>
              <i className="pipeline-step-mark" aria-hidden="true">{state === 'succeeded' ? '✓' : state === 'failed' || state === 'blocked' ? '×' : index + 1}</i>
              <strong>{label}</strong><span>{states[state]}</span>
              {event?.observed_at && <small>확인 {new Date(event.observed_at).toLocaleString('ko-KR')}</small>}
              {event?.commit_sha && <small>커밋 {event.commit_sha.slice(0, 12)}</small>}
              {event?.image_digest && <small>이미지 {event.image_digest.slice(0, 19)}…</small>}
              {event?.error_code && <small>오류 {event.error_code}</small>}
            </li>
          })}
        </ol>
        {data.recentRuns.length > 1 && <details><summary>이전 실행 {data.recentRuns.length - 1}건</summary>
          <ul>{data.recentRuns.slice(1).map(run => <li key={run.run_id}>
            {run.run_id} · {new Date(run.created_at).toLocaleString('ko-KR')}
          </li>)}</ul></details>}
      </>}
    </>}
  </section>
}
